import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormlyFieldProps as CoreFormlyFieldProps, FieldWrapper, FormlyFieldConfig } from '@ngx-formly/core';
import { BehaviorSubject } from 'rxjs';
import { AIBtnEvent } from '../../../models';

interface IsResolved {
  is_resolved: boolean;
}
export interface FormlyFieldProps extends CoreFormlyFieldProps {
  hideLabel?: boolean;
  hideRequiredMarker?: boolean;
  labelPosition?: 'floating';
  wrapClass?: string;
  labelClass?: string;
  tips?: string;
  addonLeft?: string;
  addonRight?: string;
  addonLeftOnClick?: any;
  addonRightOnClick?: any;
  feedback?: number;
  feedbackKey?: string;
  isResolved?: IsResolved[];
  wrapGroupClass?: string;
  openFeedback?: (field: FormlyFieldConfig) => void;
  activeLabelAI?: boolean;
  activeInputAI?: boolean;
  labelAIType?: 'btn-fill' | 'btn-transparent' | 'btn-icon';
  labelAILabel?: string;
  labelAIPrompt?: boolean;
  labelAIPromptLabel?: string;
  labelAIPromptPlaceholder?: string;
  inputAppendClassAI?: string[];
  aiButtonEvent?: (event: AIBtnEvent, field: FormlyFieldConfig) => void;
}

@Component({
  selector: 'formly-wrapper-form-field',
  template: `
    <ng-template #labelTemplate>
      <label
        *ngIf="props.label && props.hideLabel !== true"
        [attr.for]="id"
        [ngClass]="{
          'text-sm': !props.labelClass,
          'flex items-center gap-3': props.tips,
        }"
        [class]="props.labelClass ? props.labelClass : ''"
      >
        <span class="flex items-center gap-3">
          {{ props.label }}
          <ng-container *ngIf="props.tips">
            <svg-icon
              src="@assets/images/icons/question-mark-solid.svg"
              class="block h-5 w-5"
              tooltip="{{ props.tips }}"
            ></svg-icon>
          </ng-container>
        </span>

        <ng-container *ngIf="props.activeLabelAI">
          <ng-container [ngTemplateOutlet]="aiButton"></ng-container>
        </ng-container>
      </label>
    </ng-template>
    <div
      class="relative"
      [class.form-floating]="props.labelPosition === 'floating'"
      [class.has-error]="showError"
      [ngClass]="props.wrapClass ? props.wrapClass : 'input-group mb-3 max-sm:mb-1'"
    >
      <ng-container *ngIf="props.labelPosition !== 'floating'">
        <ng-container [ngTemplateOutlet]="labelTemplate"></ng-container>
      </ng-container>

      <ng-container *ngIf="!props.addonLeft && !props.addonRight; else inGroup">
        <ng-container *ngIf="!props.feedback; else fieldFeedback">
          <ng-template #fieldComponent></ng-template>
        </ng-container>
      </ng-container>

      <ng-template #inGroup>
        <div class="flex">
          <div [ngClass]="props.wrapGroupClass ? props.wrapGroupClass : ''" class="group relative w-full">
            <div
              #matPrefix
              (click)="addonLeftClick($event)"
              class="prefix"
              *ngIf="props.addonLeft"
              [innerHTML]="props.addonLeft"
            >
              {{ props.addonLeft }}
            </div>

            <ng-template #fieldComponent></ng-template>
            <div
              #matSuffix
              (click)="addonRightClick($event)"
              class="suffix"
              *ngIf="props.addonRight"
              [innerHTML]="props.addonRight"
            >
              {{ props.addonRight }}
            </div>
            <ng-container *ngIf="props.activeInputAI">
              <ng-container [ngTemplateOutlet]="aiButton"></ng-container>
            </ng-container>
          </div>

          <div
            id="feedback-{{ props.feedbackKey ? props.feedbackKey : key }}"
            class="feedback mt-2 ltr:ml-2 rtl:mr-2"
            *ngIf="props.feedback"
          >
            <ng-container *ngFor="let d of props.isResolved">
              <ng-container *ngIf="!d.is_resolved" [ngTemplateOutlet]="feedbackTemplate"></ng-container>
            </ng-container>
          </div>
        </div>
      </ng-template>

      <ng-container *ngIf="props.labelPosition === 'floating'">
        <ng-container [ngTemplateOutlet]="labelTemplate"></ng-container>
      </ng-container>

      <div *ngIf="showError" class="invalid-feedback" [style.display]="'block'">
        <formly-validation-message [field]="field"></formly-validation-message>
      </div>

      <small *ngIf="props.description" class="form-text text-muted">{{ props.description }}</small>

      <ng-template #fieldFeedback>
        <div *ngFor="let d of props.isResolved" class="relative flex w-full">
          <div class="relative grid w-full">
            <ng-template #fieldComponent></ng-template>
          </div>
          <div
            *ngIf="!d?.is_resolved"
            id="feedback-{{ props.feedbackKey ? props.feedbackKey : key }}"
            class="feedback mt-2 ltr:ml-2 rtl:mr-2"
          >
            <ng-container [ngTemplateOutlet]="feedbackTemplate"></ng-container>
          </div>
        </div>
      </ng-template>

      <ng-template #feedbackTemplate>
        <div class="relative">
          <div class="" [class.left-[55px]]="haveDelete | async">
            <div
              class="btn btn-light relative rounded-full border-none p-2 text-slate-500"
              (click)="openFeedback($event)"
            >
              <svg-icon
                src="@assets/images/icons/exclamation-triangle-solid.svg"
                class="btn-icon flex h-4 w-4"
              ></svg-icon>
              <span
                class="absolute -right-2 -top-2 flex h-[18px] w-[18px] items-center justify-center truncate rounded-full bg-[#FFEDD5] text-[10px] text-[#F97316]"
                >{{ props.feedback }}</span
              >
            </div>
          </div>
        </div>
      </ng-template>
      <ng-template #aiButton>
        <app-ai-button
          [type]="props.labelAIType!"
          [label]="props.labelAILabel!"
          [prompt]="props.labelAIPrompt!"
          [promptLabel]="props.labelAIPromptLabel!"
          [promptPlaceholder]="props.labelAIPromptPlaceholder!"
          [inputAppendClassAI]="props.inputAppendClassAI!"
          (btnEvent)="aiEvent($event)"
        >
        </app-ai-button>
      </ng-template>
    </div>
  `,
  styles: ['.input-group { @apply gap-1; }'],
})
export class FormlyWrapperFormField extends FieldWrapper<FormlyFieldConfig<FormlyFieldProps>> implements OnInit {
  @ViewChild('matPrefix', { static: true }) matPrefix!: TemplateRef<any>;
  @ViewChild('matSuffix', { static: true }) matSuffix!: TemplateRef<any>;

  haveDelete: BehaviorSubject<any> = new BehaviorSubject<any>(false);

  ngOnInit() {
    if (this.props.feedback) {
      const haveRepeat = this.findClosestParentWithType(this.field, 'repeat');
      if (haveRepeat && haveRepeat.props.fieldChanges) {
        haveRepeat.props.fieldChanges.subscribe((data: any) => {
          this.haveDelete.next(data.isMin() ? false : true);
        });
      }
    }
  }

  aiEvent(event: AIBtnEvent) {
    this.props.aiButtonEvent?.(event, this.field);
  }

  addonLeftClick($event: any) {
    if (this.props.addonLeftOnClick) {
      this.props.addonLeftOnClick(this.field, this, $event);
    }
  }

  addonRightClick($event: any) {
    if (this.props.addonRightOnClick) {
      this.props.addonRightOnClick(this.field, this, $event);
    }
  }

  openFeedback($event: any) {
    if (this.props.openFeedback) {
      // this.props.openFeedback(this.field, this, $event);
      this.props.openFeedback(this.field);
    }
  }

  findClosestParentWithType(obj: any, targetType: string): any {
    if (obj.type === targetType) {
      return obj;
    }
    if (obj.parent) {
      return this.findClosestParentWithType(obj.parent, targetType);
    }
    return null;
  }
}
